import { useLayoutEffect, useSyncExternalStore } from 'react';

/**
 * The creator dashboard's theme: light, which is the ivory it was drawn in, or dark.
 *
 * **It is the dashboard's and nothing else's.** The library is drawn on ink whoever is looking and
 * the public pages on ivory, and neither has a second face; so the theme is not a property of the
 * site but of one side of it. What is chosen is kept for this browser (`THEME_KEY`) — a preference
 * of whoever sits at it, like the team last worked in — and what is *shown* is a mark on the
 * document, `data-theme="dark"` on `<html>`, that is there only while a page of the dashboard is
 * (`useDashboardTheme`). On the element at the top because a menu, a dialog and a tooltip are
 * drawn outside the dashboard's own box and must follow it.
 *
 * The colours themselves are `kit/dark.css`: the same tokens the kit already draws with, given
 * other values under that mark. Nothing here knows a colour.
 */

export type Theme = 'light' | 'dark';

export const THEME_KEY = 'boardflight.theme';

/** The attribute on `<html>` that `kit/dark.css` hangs from. Absent is light. */
export const THEME_ATTRIBUTE = 'data-theme';

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** What this browser chose. Light until somebody chooses otherwise, and for anything unreadable. */
export function readTheme(from: Pick<Storage, 'getItem'> | null = storage()): Theme {
  try {
    return from?.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

const listeners = new Set<() => void>();
let chosen: Theme | null = null;

function current(): Theme {
  chosen ??= readTheme();
  return chosen;
}

/** Choose a theme: kept for this browser, and drawn at once wherever the dashboard is on screen. */
export function chooseTheme(theme: Theme): void {
  chosen = theme;
  try {
    storage()?.setItem(THEME_KEY, theme);
  } catch {
    // Storage refused: the choice holds until the page is reloaded, and that is all.
  }
  if (shown !== null) show(theme);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Another tab of the dashboard changed it: this one follows.
  const onStorage = (event: StorageEvent) => {
    if (event.key !== THEME_KEY) return;
    chosen = readTheme();
    if (shown !== null) show(chosen);
    listener();
  };
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}

/** The chosen theme, for a control that says or changes it. */
export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, current, () => 'light');
}

/* ------------------------------------------------------------------------- on the document */

/** What the document is drawn in now: null while no page of the dashboard is on it. */
let shown: Theme | null = null;
const shownListeners = new Set<() => void>();

function show(theme: Theme | null): void {
  shown = theme;
  if (typeof document !== 'undefined') {
    if (theme === 'dark') document.documentElement.setAttribute(THEME_ATTRIBUTE, 'dark');
    else document.documentElement.removeAttribute(THEME_ATTRIBUTE);
  }
  for (const listener of shownListeners) listener();
}

/** Whether the document is drawn dark at this moment — for the two things that paint in script. */
export function drawnDark(): boolean {
  return shown === 'dark';
}

/** Hear the document change between light and dark. Returns the way to stop. */
export function onDrawnChange(listener: () => void): () => void {
  shownListeners.add(listener);
  return () => shownListeners.delete(listener);
}

/**
 * Draw the document in the chosen theme for as long as the caller is mounted — the dashboard, once,
 * around every page it has (`App.tsx`). A layout effect, so the first frame of the dashboard is
 * already the right colour, and undone on the way out, so the library and the public pages are
 * never drawn in a theme that is not theirs.
 */
export function useDashboardTheme(): void {
  const theme = useTheme();
  useLayoutEffect(() => {
    show(theme);
    return () => show(null);
  }, [theme]);
}

/**
 * Before the first render: draw the document dark where the dashboard is about to be, so a dark
 * dashboard does not open on a flash of ivory while its session and its script arrive.
 *
 * A guess, and `useDashboardTheme` is what makes it true. Where the guess is wrong — the address
 * is the dashboard's and nobody is signed in — `releaseTheme` takes it back before the sign-in
 * page is drawn.
 */
export function primeTheme(onDashboard: boolean): void {
  if (onDashboard && current() === 'dark') show('dark');
}

/** Take back a theme primed for a dashboard that turned out not to be what is drawn. */
export function releaseTheme(): void {
  if (shown !== null) show(null);
}

/** For tests: forget what was chosen and what is shown. */
export function forgetTheme(): void {
  chosen = null;
  shown = null;
  listeners.clear();
  shownListeners.clear();
}
